// Global imports
import { Fragment, useMemo } from 'react'
import { useFloating } from '@floating-ui/react'
import { autoPlacement } from '@floating-ui/dom'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'
import 'react-day-picker/style.css'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { formatDate } from '~/utils'

interface Props {
  id: string
  label: string
  value: string
  /** Left padding in pixels to accommodate the label. Default: 64 */
  labelInset?: number
  allowSaturday?: boolean
  disabledDates?: Date[]
  /** Message rendered inside the calendar, below the day grid. */
  footer?: React.ReactNode
  onChange: (value: string) => void
}

// Parse a YYYY-MM-DD string to a local Date
const parseDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * DateInput
 *
 * A read-only text input with a floating calendar popover for single-date
 * selection. Uses react-day-picker for the calendar, Headless UI Popover
 * for open/close state, and @floating-ui for positioning.
 * Disabled days (past, Sunday, optionally Saturday) are enforced at the
 * picker level so invalid dates can never be selected.
 */
const DateInput = ({
  id,
  label,
  value,
  labelInset = 64,
  allowSaturday = false,
  disabledDates,
  footer,
  onChange,
}: Props) => {
  const defaultClassNames = getDefaultClassNames()

  // Floating UI positioning
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      autoPlacement({
        allowedPlacements: ['bottom-start', 'bottom-end', 'bottom'],
      }),
    ],
  })

  // Selected date for the picker
  const selected = useMemo(() => {
    return value ? parseDate(value) : undefined
  }, [value])

  // Disabled day matchers
  const disabled = useMemo(() => {
    const matchers: ({ before: Date } | { dayOfWeek: number[] } | Date)[] = [
      { before: new Date() },
      { dayOfWeek: allowSaturday ? [0] : [0, 6] },
    ]

    if (disabledDates) {
      matchers.push(...disabledDates)
    }

    return matchers
  }, [allowSaturday, disabledDates])

  // Formatted display value
  const displayValue = value ? formatDate(value) : ''

  return (
    <Popover as={Fragment}>
      {({ close }) => (
        <div className="relative">
          <PopoverButton
            ref={refs.setReference}
            as="div"
            className="relative cursor-pointer"
          >
            <label
              htmlFor={id}
              className="pointer-events-none absolute left-4 top-1/2 mb-0 -translate-y-1/2 text-neutral-500"
            >
              {label}
            </label>
            <input
              id={id}
              type="text"
              readOnly
              style={{ paddingLeft: `${labelInset}px` }}
          className="w-full cursor-pointer appearance-none rounded-lg border border-neutral-300 bg-white py-3 pr-10 focus:border-teal-green focus:outline-none"
              value={displayValue}
              placeholder="Select date"
            />
            <CalendarDaysIcon className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-neutral-400" />
          </PopoverButton>
          <PopoverPanel
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-50 mt-1 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
          >
            <DayPicker
              mode="single"
              required={false}
              selected={selected}
              onSelect={(day) => {
                if (!day) return

                onChange(formatDate(day, 'input'))

                close()
              }}
              disabled={disabled}
              defaultMonth={selected ?? new Date()}
              numberOfMonths={1}
              footer={footer}
              style={{
                '--rdp-accent-color': '#269D84',
                '--rdp-accent-background-color': '#269D84',
                '--rdp-outline': 'none',
                '--rdp-outline-selected': 'none',
              } as React.CSSProperties}
              classNames={{
                root: `${defaultClassNames.root} rdp-custom`,
                footer: `${defaultClassNames.footer} !mt-1 !border-t !border-neutral-200 !px-1 !pt-3 !text-sm`,
                today: `${defaultClassNames.today} !font-bold !text-forest-green`,
                selected: `${defaultClassNames.selected}`,
                day: `${defaultClassNames.day} !rounded-full focus:!outline-none focus-visible:!outline-none`,
                chevron: `${defaultClassNames.chevron} !fill-forest-green`,
                nav: `${defaultClassNames.nav}`,
                button_next: `${defaultClassNames.button_next} hover:!bg-forest-green/10 focus:!outline-none focus-visible:!outline-none`,
                button_previous: `${defaultClassNames.button_previous} hover:!bg-forest-green/10 focus:!outline-none focus-visible:!outline-none`,
              }}
            />
          </PopoverPanel>
        </div>
      )}
    </Popover>
  )
}

export default DateInput
